import React from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';

const ConstructionQuestionnaire = ({ 
  constructionTrack, 
  onConstructionTrackChange,
  projectTypes,
  onProjectTypesChange,
  projectScale,
  onProjectScaleChange
}) => {
  const constructionTrackOptions = [
    { value: 'building', label: 'Building Construction', description: 'Residential, commercial, institutional' },
    { value: 'civil', label: 'Civil Engineering', description: 'Infrastructure, roads, bridges' },
    { value: 'industrial', label: 'Industrial Projects', description: 'Manufacturing, energy, utilities' }
  ];

  const buildingProjectTypes = [
    { id: 'residential', label: 'Residential Construction' },
    { id: 'commercial', label: 'Commercial Buildings' },
    { id: 'institutional', label: 'Institutional Projects' },
    { id: 'mixed-use', label: 'Mixed-Use Developments' },
    { id: 'renovation', label: 'Renovation & Retrofit' },
    { id: 'high-rise', label: 'High-Rise Construction' }
  ];

  const civilProjectTypes = [
    { id: 'roads', label: 'Roads & Highways' },
    { id: 'bridges', label: 'Bridges & Structures' },
    { id: 'water', label: 'Water & Wastewater' },
    { id: 'transit', label: 'Transit Infrastructure' },
    { id: 'municipal', label: 'Municipal Projects' },
    { id: 'environmental', label: 'Environmental Engineering' }
  ];

  const industrialProjectTypes = [
    { id: 'manufacturing', label: 'Manufacturing Facilities' },
    { id: 'energy', label: 'Energy & Power' },
    { id: 'oil-gas', label: 'Oil & Gas' },
    { id: 'mining', label: 'Mining & Resources' },
    { id: 'utilities', label: 'Utilities Infrastructure' },
    { id: 'heavy-industrial', label: 'Heavy Industrial' }
  ];

  const projectScaleOptions = [
    { value: 'small', label: 'Small Projects', description: 'Under $5M' },
    { value: 'medium', label: 'Medium Projects', description: '$5M - $50M' },
    { value: 'large', label: 'Large Projects', description: '$50M - $200M' },
    { value: 'mega', label: 'Mega Projects', description: '$200M+' }
  ];

  const getProjectTypesForTrack = () => {
    switch (constructionTrack) {
      case 'building': return buildingProjectTypes;
      case 'civil': return civilProjectTypes;
      case 'industrial': return industrialProjectTypes;
      default: return [];
    }
  };

  const handleProjectTypeToggle = (typeId) => {
    const newTypes = projectTypes?.includes(typeId)
      ? projectTypes?.filter(t => t !== typeId)
      : [...projectTypes, typeId];
    onProjectTypesChange(newTypes);
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
        Construction Specialization
      </h2>
      <p className="text-sm md:text-base lg:text-lg text-muted-foreground mb-6 md:mb-8">
        Tell us about your construction expertise and project experience
      </p>
      <div className="space-y-6">
        <Select
          label="Primary Construction Track"
          description="Select your main area of construction expertise"
          options={constructionTrackOptions}
          value={constructionTrack}
          onChange={onConstructionTrackChange}
          required
        />

        {constructionTrack && (
          <>
            <CheckboxGroup label="Project Types & Expertise">
              {getProjectTypesForTrack()?.map((type) => (
                <Checkbox
                  key={type?.id}
                  label={type?.label}
                  checked={projectTypes?.includes(type?.id)}
                  onChange={() => handleProjectTypeToggle(type?.id)}
                />
              ))}
            </CheckboxGroup>

            <Select
              label="Typical Project Scale"
              description="The scale of projects you typically manage or work on"
              options={projectScaleOptions}
              value={projectScale}
              onChange={onProjectScaleChange}
              required
            />
          </>
        )}

        <div className="p-4 md:p-6 rounded-xl bg-accent/10 border border-accent/20">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                <span className="text-lg">🏗️</span>
              </div>
            </div>
            <div>
              <p className="text-sm md:text-base text-foreground font-medium mb-1">
                Project Experience Matters
              </p>
              <p className="text-sm md:text-base text-muted-foreground">
                Your project scale and type experience helps us match you with opportunities that align with your expertise and career goals.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstructionQuestionnaire;